/**
 * 重大资产**六个动作对话框**（R1 台账 / R2 估值 / R6 三个资金动作 / R8 处置）· 批 92
 *
 * 依据：`R域PRD` §5 · `AC-R1.2`–`R1.4` · `AC-R2.4` · `AC-R6.1`–`R6.5` · `AC-R7.1` · `AC-R8.1`–`R8.4` ·
 * `DR-044`（期限块可空）· `DR-053` D13/D16/D17 · 原型 `16-重大资产_原型.html:449–703` ·
 * 铁律 63（可选键剔键）· 100（`Checkbox/Switch` 的 `inputProps` 不收 `data-*`）。
 *
 * ## 🔴 为什么每个 `Body` 单独导出
 *
 * `Dialog` 走 Portal，`renderToStaticMarkup` 读不到它的内容 ⇒ **断言必须打在内容件上**
 * （与 `RestoreDialogBody` / `ReimburseDialogBody` / `GoalMoveDialogBody` 同范式）。
 *
 * ## 🔴 三个「不在端侧判」的边界
 *
 * | 判断 | 谁判 |
 * |---|---|
 * | 日期是不是**真实存在**的（`2026-13-01`） | **服务端**（`assertRealDay` · `AC-R2.4`）|
 * | 首付 > 总价、有贷款部分却没贷款载体 | **服务端**（`purchase()` 里判）；端侧只拦"一眼可见的输入错误" |
 * | 收益分类是否合法 | **契约** `MAJOR_ASSET_INCOME_CATEGORY_IDS`（端侧只做两选一） |
 *
 * ## ⚠️ 六个对话框的**金额输入一律是"元文本"**
 *
 * 提交时才经 `features/major-asset.ts` 的 `buildXxx()` 换成整数分 —— 页面层与纯逻辑层
 * 的分工与 `txn-draft` / `account-draft` 完全一致（输入框里永远是字符串）。
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  DepMethod,
  MajorAssetType,
  MAJOR_ASSET_INCOME_CATEGORY_IDS,
  RepaymentMethod,
  type AccountView,
  type LedgerMemberView,
  type MajorAssetDetailView,
} from '@jiaying/shared';
import type {
  AppendDraft,
  AssetFormDraft,
  DisposeDraft,
  DraftErrors,
  IncomeDraft,
  PurchaseDraft,
  ValuationDraft,
} from '../features/major-asset.js';
import {
  BTN_APPEND,
  BTN_CANCEL,
  BTN_DISPOSE,
  BTN_INCOME,
  BTN_PURCHASE,
  BTN_SAVE,
  BTN_SAVE_VALUATION,
  DEP_LABEL,
  DLG_APPEND_TITLE,
  DLG_ASSET_TITLE_CREATE,
  DLG_ASSET_TITLE_EDIT,
  DLG_DISPOSE_TITLE,
  DLG_INCOME_TITLE,
  DLG_LOANS_TITLE,
  DLG_PURCHASE_TITLE,
  DLG_VALUATION_TITLE,
  F_AMOUNT,
  F_ANNUAL_RATE,
  F_AREA,
  F_CATEGORY,
  F_CURRENCY,
  F_DATE,
  F_DEP_METHOD,
  F_DEP_YEARS,
  F_DOWN_PAYMENT,
  F_DUE_DATE,
  F_ESTIMATED,
  F_FROM_ACCOUNT,
  F_HIDDEN,
  F_LOAN_ACCOUNT,
  F_LOAN_AMOUNT_HINT,
  F_LOCATION,
  F_METHOD,
  F_NAME,
  F_NEW_LOAN,
  F_NEW_VALUE,
  F_NOTE,
  F_OWNER,
  F_PROCEEDS,
  F_PURCHASE_COST,
  F_PURCHASE_DATE,
  F_SETTLE_LOANS,
  F_START_DATE,
  F_TERMS,
  F_TO_ACCOUNT,
  F_TOTAL,
  F_TYPE,
  F_VALUATION_DATE,
  HINT_APPEND,
  HINT_ASSET_ESTIMATED,
  HINT_ASSET_MIN,
  HINT_DISPOSE,
  HINT_INCOME,
  HINT_LOANS,
  HINT_PURCHASE,
  HINT_VALUATION,
  INCOME_CATEGORY_LABEL,
  TYPE_LABEL,
  TYPE_ORDER,
} from '../features/major-asset-labels.js';
import { AccountPicker } from './AccountPicker.js';

/** 本文件的错误袋类型（与纯逻辑层同形） */

// ─────────────────────────── ① 台账：添加 / 编辑 ───────────────────────────

export interface AssetFormBodyProps {
  draft: AssetFormDraft;
  errors: DraftErrors;
  busy: boolean;
  mode: 'create' | 'edit';
  members: readonly LedgerMemberView[];
  onChange: (patch: Partial<AssetFormDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function AssetFormBody({
  draft,
  errors,
  busy,
  mode,
  members,
  onChange,
  onSubmit,
  onCancel,
}: AssetFormBodyProps) {
  const showDepYears = draft.depMethod === DepMethod.Straight;
  return (
    <>
      <DialogTitle data-testid="ma-form-title">
        {mode === 'create' ? DLG_ASSET_TITLE_CREATE : DLG_ASSET_TITLE_EDIT}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {F_TYPE}
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={draft.type}
              onChange={(_e, v: MajorAssetType | null) => {
                if (v !== null) onChange({ type: v });
              }}
            >
              {TYPE_ORDER.map((t) => (
                <ToggleButton key={t} value={t} data-testid={`ma-form-type-${t}`}>
                  {TYPE_LABEL[t]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TextField
            label={F_NAME}
            required
            size="small"
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            error={errors.name !== undefined}
            helperText={errors.name}
            slotProps={{ htmlInput: { 'data-testid': 'ma-form-name' } }}
          />

          <TextField
            select
            label={F_OWNER}
            required
            size="small"
            value={draft.ownerId}
            onChange={(e) => onChange({ ownerId: e.target.value })}
            error={errors.ownerId !== undefined}
            helperText={errors.ownerId}
            data-testid="ma-form-owner"
          >
            {members.map((m) => (
              <MenuItem key={m.userId} value={m.userId}>
                {m.name}
                {m.isSelf ? '（我）' : ''}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label={F_PURCHASE_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.purchaseDate}
              onChange={(e) => onChange({ purchaseDate: e.target.value })}
              error={errors.purchaseDate !== undefined}
              helperText={errors.purchaseDate}
            />
            <TextField
              label={F_CURRENCY}
              size="small"
              sx={{ width: 110 }}
              value={draft.currency}
              onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={F_PURCHASE_COST}
              size="small"
              fullWidth
              value={draft.purchaseCostText}
              onChange={(e) => onChange({ purchaseCostText: e.target.value })}
              error={errors.purchaseCost !== undefined}
              helperText={errors.purchaseCost}
            />
            <TextField
              label={F_ESTIMATED}
              size="small"
              fullWidth
              value={draft.estimatedValueText}
              onChange={(e) => onChange({ estimatedValueText: e.target.value })}
              error={errors.estimatedValue !== undefined}
              helperText={errors.estimatedValue}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {HINT_ASSET_MIN} · {HINT_ASSET_ESTIMATED}
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label={F_DEP_METHOD}
              size="small"
              fullWidth
              value={draft.depMethod}
              onChange={(e) => onChange({ depMethod: e.target.value as DepMethod })}
            >
              <MenuItem value={DepMethod.None}>{DEP_LABEL[DepMethod.None]}</MenuItem>
              <MenuItem value={DepMethod.Straight}>{DEP_LABEL[DepMethod.Straight]}</MenuItem>
            </TextField>
            {showDepYears && (
              <TextField
                label={F_DEP_YEARS}
                size="small"
                fullWidth
                value={draft.depYearsText}
                onChange={(e) => onChange({ depYearsText: e.target.value })}
                error={errors.depYears !== undefined}
                helperText={errors.depYears}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={F_LOCATION}
              size="small"
              fullWidth
              value={draft.location}
              onChange={(e) => onChange({ location: e.target.value })}
            />
            <TextField
              label={F_AREA}
              size="small"
              sx={{ width: 140 }}
              value={draft.areaText}
              onChange={(e) => onChange({ areaText: e.target.value })}
              error={errors.area !== undefined}
              helperText={errors.area}
            />
          </Stack>

          <TextField
            label={F_NOTE}
            size="small"
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => onChange({ note: e.target.value })}
          />

          <FormControlLabel
            control={
              <Switch
                checked={draft.hidden}
                onChange={(e) => onChange({ hidden: e.target.checked })}
                slotProps={{ input: { 'aria-label': F_HIDDEN } }}
              />
            }
            label={<Typography variant="body2">{F_HIDDEN}</Typography>}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="ma-form-submit">
          {BTN_SAVE}
        </Button>
      </DialogActions>
    </>
  );
}

export function AssetFormDialog(
  props: Omit<AssetFormBodyProps, 'onCancel'> & { open: boolean; onClose: () => void },
) {
  const { open, onClose, ...body } = props;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <AssetFormBody {...body} onCancel={onClose} />
    </Dialog>
  );
}

// ─────────────────────────── ② 调整估值 ───────────────────────────

export interface ValuationBodyProps {
  draft: ValuationDraft;
  errors: DraftErrors;
  busy: boolean;
  assetName: string;
  onChange: (patch: Partial<ValuationDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function ValuationBody({
  draft,
  errors,
  busy,
  assetName,
  onChange,
  onSubmit,
  onCancel,
}: ValuationBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_VALUATION_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={F_NEW_VALUE}
              size="small"
              fullWidth
              value={draft.valueText}
              onChange={(e) => onChange({ valueText: e.target.value })}
              error={errors.value !== undefined}
              helperText={errors.value}
              slotProps={{ htmlInput: { 'data-testid': 'ma-valuation-value' } }}
            />
            <TextField
              label={F_VALUATION_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.date}
              onChange={(e) => onChange({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
            />
          </Stack>
          <TextField
            label={F_NOTE}
            size="small"
            value={draft.note}
            onChange={(e) => onChange({ note: e.target.value })}
            placeholder="例：周边成交价参考"
          />
          <Alert severity="info" variant="outlined">
            {HINT_VALUATION}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={busy}
          data-testid="ma-valuation-submit"
        >
          {BTN_SAVE_VALUATION}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────── ③ 购入 ───────────────────────────

export interface PurchaseBodyProps {
  draft: PurchaseDraft;
  errors: DraftErrors;
  busy: boolean;
  assetName: string;
  accounts: readonly AccountView[];
  /** 可关联的贷款账户（`contra(loan)`） */
  loanAccounts: readonly AccountView[];
  onChange: (patch: Partial<PurchaseDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function PurchaseBody({
  draft,
  errors,
  busy,
  assetName,
  accounts,
  loanAccounts,
  onChange,
  onSubmit,
  onCancel,
}: PurchaseBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_PURCHASE_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={F_TOTAL}
              size="small"
              fullWidth
              value={draft.totalText}
              onChange={(e) => onChange({ totalText: e.target.value })}
              error={errors.total !== undefined}
              helperText={errors.total}
              slotProps={{ htmlInput: { 'data-testid': 'ma-purchase-total' } }}
            />
            <TextField
              label={F_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.date}
              onChange={(e) => onChange({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={F_DOWN_PAYMENT}
              size="small"
              fullWidth
              value={draft.downText}
              onChange={(e) => onChange({ downText: e.target.value })}
              error={errors.down !== undefined}
              helperText={errors.down}
            />
            <Box sx={{ width: '100%' }}>
              <AccountPicker
                accounts={accounts}
                value={draft.fromAccountId}
                onChange={(id) => onChange({ fromAccountId: id })}
                label={F_FROM_ACCOUNT}
                error={errors.fromAccountId !== undefined}
                helperText={errors.fromAccountId}
                required
              />
            </Box>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {HINT_PURCHASE} · {F_LOAN_AMOUNT_HINT}
          </Typography>

          <Divider />

          <TextField
            select
            label={F_LOAN_ACCOUNT}
            size="small"
            value={draft.loanAccountId}
            onChange={(e) => onChange({ loanAccountId: e.target.value })}
            helperText="有贷款部分时：选已有贷款账户，或在下面新建一个"
          >
            <MenuItem value="">
              <em>不关联已有贷款账户</em>
            </MenuItem>
            {loanAccounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={F_NEW_LOAN}
            size="small"
            value={draft.newLoanName}
            onChange={(e) => onChange({ newLoanName: e.target.value })}
            error={errors.newLoanName !== undefined}
            helperText={errors.newLoanName ?? '填了名字才会新建；一次性新建后自动挂到本资产'}
          />

          {draft.newLoanName.trim() !== '' && (
            <>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={F_ANNUAL_RATE}
                  size="small"
                  fullWidth
                  value={draft.annualRateText}
                  onChange={(e) => onChange({ annualRateText: e.target.value })}
                  error={errors.annualRate !== undefined}
                  helperText={errors.annualRate}
                  placeholder="可空，如 3.85"
                />
                <TextField
                  label={F_TERMS}
                  size="small"
                  fullWidth
                  value={draft.termsText}
                  onChange={(e) => onChange({ termsText: e.target.value })}
                  error={errors.terms !== undefined}
                  helperText={errors.terms}
                  placeholder="可空"
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={F_START_DATE}
                  size="small"
                  fullWidth
                  placeholder="YYYY-MM-DD（可空）"
                  value={draft.startDate}
                  onChange={(e) => onChange({ startDate: e.target.value })}
                />
                <TextField
                  label={F_DUE_DATE}
                  size="small"
                  fullWidth
                  placeholder="YYYY-MM-DD（可空）"
                  value={draft.dueDate}
                  onChange={(e) => onChange({ dueDate: e.target.value })}
                />
              </Stack>
              <TextField
                select
                label={F_METHOD}
                size="small"
                value={draft.method}
                onChange={(e) => onChange({ method: e.target.value as '' | RepaymentMethod })}
              >
                <MenuItem value="">
                  <em>可空</em>
                </MenuItem>
                <MenuItem value={RepaymentMethod.EqualInstallment}>等额本息</MenuItem>
                <MenuItem value={RepaymentMethod.EqualPrincipal}>等额本金</MenuItem>
              </TextField>
              <Typography variant="caption" color="text.secondary">
                期限块六项**全可空**（`DR-044`）：不齐全也是合法状态，参数齐才会生成还款计划
              </Typography>
            </>
          )}

          <TextField
            label={F_NOTE}
            size="small"
            value={draft.note}
            onChange={(e) => onChange({ note: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={busy}
          data-testid="ma-purchase-submit"
        >
          {BTN_PURCHASE}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────── ④ 追加投资 ───────────────────────────

export interface AppendBodyProps {
  draft: AppendDraft;
  errors: DraftErrors;
  busy: boolean;
  assetName: string;
  accounts: readonly AccountView[];
  onChange: (patch: Partial<AppendDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function AppendBody({
  draft,
  errors,
  busy,
  assetName,
  accounts,
  onChange,
  onSubmit,
  onCancel,
}: AppendBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_APPEND_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={F_AMOUNT}
              size="small"
              fullWidth
              value={draft.amountText}
              onChange={(e) => onChange({ amountText: e.target.value })}
              error={errors.amount !== undefined}
              helperText={errors.amount}
              slotProps={{ htmlInput: { 'data-testid': 'ma-append-amount' } }}
            />
            <TextField
              label={F_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.date}
              onChange={(e) => onChange({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
            />
          </Stack>
          <AccountPicker
            accounts={accounts}
            value={draft.fromAccountId}
            onChange={(id) => onChange({ fromAccountId: id })}
            label={F_FROM_ACCOUNT}
            error={errors.fromAccountId !== undefined}
            helperText={errors.fromAccountId}
            required
          />
          <TextField
            label={F_NOTE}
            size="small"
            value={draft.note}
            onChange={(e) => onChange({ note: e.target.value })}
            placeholder="例：厨房改造 / 装修"
          />
          <Alert severity="info" variant="outlined">
            {HINT_APPEND}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="ma-append-submit">
          {BTN_APPEND}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────── ⑤ 记收益 ───────────────────────────

export interface IncomeBodyProps {
  draft: IncomeDraft;
  errors: DraftErrors;
  busy: boolean;
  assetName: string;
  accounts: readonly AccountView[];
  onChange: (patch: Partial<IncomeDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function IncomeBody({
  draft,
  errors,
  busy,
  assetName,
  accounts,
  onChange,
  onSubmit,
  onCancel,
}: IncomeBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_INCOME_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={F_AMOUNT}
              size="small"
              fullWidth
              value={draft.amountText}
              onChange={(e) => onChange({ amountText: e.target.value })}
              error={errors.amount !== undefined}
              helperText={errors.amount}
              slotProps={{ htmlInput: { 'data-testid': 'ma-income-amount' } }}
            />
            <TextField
              label={F_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.date}
              onChange={(e) => onChange({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
            />
          </Stack>
          <AccountPicker
            accounts={accounts}
            value={draft.toAccountId}
            onChange={(id) => onChange({ toAccountId: id })}
            label={F_TO_ACCOUNT}
            error={errors.toAccountId !== undefined}
            helperText={errors.toAccountId}
            required
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {F_CATEGORY}
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={draft.categoryId}
              onChange={(_e, v: string | null) => {
                if (v !== null) onChange({ categoryId: v });
              }}
            >
              {/* 🔴 两项的值来自**契约白名单**，端侧不另写一份 id */}
              {MAJOR_ASSET_INCOME_CATEGORY_IDS.map((id) => (
                <ToggleButton key={id} value={id} data-testid={`ma-income-cat-${id}`}>
                  {INCOME_CATEGORY_LABEL[id] ?? id}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Alert severity="success" variant="outlined">
            {HINT_INCOME}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="ma-income-submit">
          {BTN_INCOME}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────── ⑥ 处置 ───────────────────────────

export interface DisposeBodyProps {
  draft: DisposeDraft;
  errors: DraftErrors;
  busy: boolean;
  assetName: string;
  accounts: readonly AccountView[];
  hasLoans: boolean;
  /** 处置不可逆 ⇒ 第一次点击只进入"再点一次"态（同 `AC-Q2.5` 核销范式） */
  confirming: boolean;
  onChange: (patch: Partial<DisposeDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function DisposeBody({
  draft,
  errors,
  busy,
  assetName,
  accounts,
  hasLoans,
  confirming,
  onChange,
  onSubmit,
  onCancel,
}: DisposeBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_DISPOSE_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={F_PROCEEDS}
              size="small"
              fullWidth
              value={draft.proceedsText}
              onChange={(e) => onChange({ proceedsText: e.target.value })}
              error={errors.proceeds !== undefined}
              helperText={errors.proceeds}
              slotProps={{ htmlInput: { 'data-testid': 'ma-dispose-proceeds' } }}
            />
            <TextField
              label={F_DATE}
              size="small"
              fullWidth
              placeholder="YYYY-MM-DD"
              value={draft.date}
              onChange={(e) => onChange({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
            />
          </Stack>
          <AccountPicker
            accounts={accounts}
            value={draft.toAccountId}
            onChange={(id) => onChange({ toAccountId: id })}
            label={`${F_TO_ACCOUNT}（实得 > 0 时必填）`}
            error={errors.toAccountId !== undefined}
            helperText={errors.toAccountId}
            allowEmpty
          />
          {hasLoans && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.settleLoans}
                  onChange={(e) => onChange({ settleLoans: e.target.checked })}
                  slotProps={{ input: { 'aria-label': F_SETTLE_LOANS } }}
                />
              }
              label={<Typography variant="body2">{F_SETTLE_LOANS}</Typography>}
            />
          )}
          <Alert severity="warning" variant="outlined">
            {HINT_DISPOSE}
          </Alert>
          {confirming && (
            <Alert severity="error" data-testid="ma-dispose-confirm-hint">
              处置**不可撤销**：再点一次「{BTN_DISPOSE}」才会执行。
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onSubmit}
          disabled={busy}
          data-testid="ma-dispose-submit"
        >
          {confirming ? '再点一次确认' : BTN_DISPOSE}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────── ⑦ 关联贷款（整组替换） ───────────────────────────

export interface LoansBodyProps {
  busy: boolean;
  assetName: string;
  loanAccounts: readonly AccountView[];
  selected: readonly string[];
  onToggle: (accountId: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function LoansBody({
  busy,
  assetName,
  loanAccounts,
  selected,
  onToggle,
  onSubmit,
  onCancel,
}: LoansBodyProps) {
  return (
    <>
      <DialogTitle>{`${DLG_LOANS_TITLE} · ${assetName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            {HINT_LOANS}
          </Typography>
          {loanAccounts.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              账本里还没有贷款类账户 —— 先在「资产 · 债权债务」里建一个。
            </Typography>
          )}
          {loanAccounts.map((a) => (
            <FormControlLabel
              key={a.id}
              data-testid={`ma-loan-option-${a.id}`}
              control={
                <Checkbox
                  checked={selected.includes(a.id)}
                  onChange={() => onToggle(a.id)}
                  slotProps={{ input: { 'aria-label': a.name } }}
                />
              }
              label={<Typography variant="body2">{a.name}</Typography>}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {BTN_CANCEL}
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="ma-loans-submit">
          {BTN_SAVE}
        </Button>
      </DialogActions>
    </>
  );
}

/** 关联贷款对话框（外层包装） */
export function LoansDialog(props: LoansBodyProps & { open: boolean; onClose: () => void }) {
  const { open, onClose, ...body } = props;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <LoansBody {...body} onCancel={onClose} />
    </Dialog>
  );
}

/** 供页面复用：详情视图 → 预选中的贷款 id（避免页面里再写一遍 `map`） */
export function selectedLoanIdsOf(view: MajorAssetDetailView | undefined): string[] {
  return view === undefined ? [] : view.loans.map((l) => l.accountId);
}


